import React, { useContext, useDeferredValue, useMemo, useState } from 'react';

import { deliveryInstructionApis } from '@apis/deliveryInstructionApis';
import { dashboardArrivalNoticeHooks } from '@apis/next/arrivalNoticeApis';
import { dashboardCustomsInstructionApis } from '@apis/next/customsInstructionApis';
import {
  dashboardCustomsRequestApis,
  dashboardCustomsRequestURLSubmitApis,
} from '@apis/next/customsRequestApis';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardPartnerApis } from '@apis/next/partnerApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { workdayApis } from '@apis/next/workday';
import { CustomsRequestSubmissionTargetInput } from '@components/admin/booking/CustomsRequestSubmissionTargetInput';
import {
  CustomsRequestFormDialogContext,
  type CustomsRequestFormDialogContextValues,
  type CustomsRequestFormDialogContextValuesBase,
} from '@components/admin/booking/helpers';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { DebugForm } from '@components/NextForms/DebugForm';
import { IndexedLabel, IndexedFormContainer } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import {
  allCustomsRequestSubmissionInternalTargets,
  customsRequestStrings,
  resolveCustomsRequestInitialValues,
} from '@data/customsRequest';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useUser } from '@hooks/queries/useUser';
import { useFormUtils } from '@hooks/useFormUtils';
import { CustomsRequestForm, CustomsRequestHelper } from '@models/next/CustomsRequest';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { mapOptional } from '@utils/types';
import { useShallowMemo } from '@utils/useShallowMemo';
import copy from 'clipboard-copy';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { Document, Page } from 'react-pdf';

const strings = createStrings({
  ja: {
    title: () => ({
      create: `${customsRequestStrings.customsRequest}を作成する`,
      update: `${customsRequestStrings.customsRequest}を編集する`,
    }),
    submit: () => ({
      create: '一時保存',
      update: 'OK',
    }),
    request: '通関依頼',
    cannotCreateCustomsRequest: '下記の原因で通関依頼書を作成できません：',
    inputPrompt: '下記情報を入力してください',
    preview: 'プレビュー',
    selectSubmissionTargetCompanyPrompt: '送信先を選択してください',
    copyLink: 'リンクをコピー',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  form: {
    ...common.flexColumn,
    flexShrink: 1,
    minHeight: 0,
  },
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1.5rem 100px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'scroll',
    maxHeight: 550,
  },
}));

const CustomsRequestFormDialogContent: React.FC = observer(() => {
  const {
    type,
    initialValues,
    onClose,
    bookingInquiry,
    arrivalNotice,
    preAlert,
    customsInstruction,
    onSuccess,
  } = useContext(CustomsRequestFormDialogContext);

  const user = useUser();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(CustomsRequestForm, {
    initialOutput: initialValues,
  });

  const { fields } = form.root;

  const partner = dashboardPartnerApis.useGet('/dashboard/partner');

  const submissionTargets = useMemo(
    () =>
      partner.data
        ? [
            ...allCustomsRequestSubmissionInternalTargets,
            ...partner.data.map(CustomsRequestHelper.mapPartnerToSubmissionTarget),
          ]
        : allCustomsRequestSubmissionInternalTargets,
    [partner.data, allCustomsRequestSubmissionInternalTargets],
  );

  const patchDashboardCustomsInstruction = dashboardCustomsInstructionApis.usePatch(
    '/dashboard/customs-instruction/:d2d_no',
    {
      params: {
        d2d_no: bookingInquiry!.d2d_no,
      },
    },
  );

  const postPreviewPdf = dashboardPdfApis.usePost('/dashboard/pdf/preview/CustomsRequest');

  const queryClient = useQueryClient();

  const patchCustomsRequest = dashboardCustomsRequestApis.usePatch(
    '/dashboard/customs-request/:d2d_no',
    {
      params: {
        d2d_no: bookingInquiry!.d2d_no,
      },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCustomsRequestApis.getKeyByPath('get', '/dashboard/customs-request/:d2d_no'),
        );
        queryClient.invalidateQueries(
          dashboardPdfApis.getKeyByPath('get', '/dashboard/pdf/CustomsRequest/:d2d_no'),
        );
      },
    },
  );

  const postCustomsRequest = dashboardCustomsRequestApis.usePost(
    '/dashboard/customs-request',
    undefined,
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCustomsRequestApis.getKeyByPath('get', '/dashboard/customs-request/:d2d_no'),
        );
        queryClient.invalidateQueries(
          dashboardPdfApis.getKeyByPath('get', '/dashboard/pdf/CustomsRequest/:d2d_no'),
        );
      },
    },
  );

  const putCustomsRequest = dashboardCustomsRequestApis.usePut(
    '/dashboard/customs-request/:d2d_no',
    {
      queries: { action: 'SUBMIT' },
      params: { d2d_no: bookingInquiry!.d2d_no },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCustomsRequestApis.getKeyByPath('get', '/dashboard/customs-request/:d2d_no'),
        );
        queryClient.invalidateQueries(
          dashboardPdfApis.getKeyByPath('get', '/dashboard/pdf/CustomsRequest/:d2d_no'),
        );
      },
    },
  );

  const customsRequestURLSubmit = dashboardCustomsRequestURLSubmitApis.usePut(
    '/dashboard/customs-request/:d2d_no',
    {
      queries: {
        action: 'URL_SUBMIT',
      },
      params: { d2d_no: bookingInquiry!.d2d_no },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCustomsRequestApis.getKeyByPath('get', '/dashboard/customs-request/:d2d_no'),
        );
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [
      patchDashboardCustomsInstruction,
      postPreviewPdf,
      patchCustomsRequest,
      postCustomsRequest,
      putCustomsRequest,
      customsRequestURLSubmit,
    ],
  });

  const pdfUrl = useMemo(() => {
    if (postPreviewPdf.data) {
      return URL.createObjectURL(postPreviewPdf.data);
    }
  }, [postPreviewPdf.data]);

  const [step, setStep] = useState(0);

  const renderFormStep0 = () => (
    <div className={classes.dialogContent}>
      {renderAlertsAndSpinnerModal()}

      <div className={clsx(classes.colSpanFull, classes.middleText, classes.textGray700)}>
        {strings.inputPrompt}
      </div>

      <IndexedFormContainer>
        <IndexedLabel index={1} field={fields.cargo_move_in_date} />
        <div>
          <TextInput field={fields.cargo_move_in_date} />
        </div>

        <IndexedLabel index={2} field={fields.free_time} />
        <div>
          <TextInput field={fields.free_time} />
        </div>

        <IndexedLabel index={3} field={fields.tentative_pickup_date} />
        <div>
          <TextInput field={fields.tentative_pickup_date} />
        </div>

        <IndexedLabel index={4} field={fields.container_no} />
        <div>
          <TextInput field={fields.container_no} />
        </div>

        <IndexedLabel index={6} field={fields.owner_company.fields.importer_code} />
        <div>
          <TextInput field={fields.owner_company.fields.importer_code} />
        </div>

        <IndexedLabel index={7} field={fields.owner_company.fields.real_time_account} />
        <div>
          <TextInput field={fields.owner_company.fields.real_time_account} />
        </div>

        <IndexedLabel index={8} field={fields.owner_company.fields.deferred_payment_account} />
        <div>
          <TextInput field={fields.owner_company.fields.deferred_payment_account} />
        </div>

        <IndexedLabel index={9} field={fields.special_note} />
        <div>
          <TextInput field={fields.special_note} multiline rows={3} />
        </div>

        <IndexedLabel index={10} field={fields.memo} />
        <div>
          <TextInput field={fields.memo} multiline rows={10} />
        </div>
      </IndexedFormContainer>
    </div>
  );

  const renderFormStep1 = () => {
    return (
      <div className={classes.dialogContent}>
        {renderAlertsAndSpinnerModal()}

        <IndexedFormContainer>
          <IndexedLabel field={fields.submission_target_company} />
          <div>
            <CustomsRequestSubmissionTargetInput
              field={fields.submission_target_company}
              placeholder={strings.selectSubmissionTargetCompanyPrompt}
              submissionTargets={submissionTargets}
            />
          </div>
        </IndexedFormContainer>
      </div>
    );
  };

  const renderFormStep2 = () => (
    <div className={classes.dialogContent}>
      {renderAlertsAndSpinnerModal()}

      <div className={classes.flexColumnCenter}>
        {pdfUrl && (
          <Document
            file={pdfUrl}
            loading={
              <div className={classes.flexColumnCenter} style={{ height: 550 }}>
                Loading...
              </div>
            }
          >
            <Page width={900} pageNumber={1} />
            <Page width={900} pageNumber={2} />
          </Document>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ActionableDialogTitle textAlign="center">{strings.title()[type]}</ActionableDialogTitle>

      <form
        {...form.bindForm({
          async onSubmit(values) {
            const createOrUpdate = async () => {
              if (type === 'update') {
                await patchCustomsRequest.mutateAsync({
                  ...values,
                });
              } else {
                await patchDashboardCustomsInstruction.mutateAsync({
                  status_name: '確認済み',
                });

                await postCustomsRequest.mutateAsync({
                  d2d_no: bookingInquiry!.d2d_no,
                  ...values,
                });
              }
            };

            const submission_target = submissionTargets.find(
              (t) => t.company === values.submission_target_company,
            );

            if (step === 0) {
              setStep(step + 1);
            } else if (step === 1) {
              const deliveryInstruction = await deliveryInstructionApis.get(
                '/dashboard/delivery-instruction/{d2d_no}/',
                {
                  path: { d2d_no: bookingInquiry?.d2d_no! },
                },
              );

              const delivery_request = await mapOptional(
                deliveryInstruction[200]?.delivery_requests[0]?.id,
                (id) =>
                  dashboardDeliveryRequestApis.client.get('/dashboard/delivery-request/:id', {
                    params: { id },
                  }),
              );

              await postPreviewPdf.mutateAsync({
                ...values,
                submission_target,
                booking_inquiry: {
                  ...bookingInquiry!,
                  owner_company: bookingInquiry!.owner_company && {
                    ...bookingInquiry!.owner_company,
                    status: bookingInquiry!.owner_company?.status ?? '未対応',
                    member_no: bookingInquiry!.owner_company?.member_no ?? '',
                  },
                },
                arrival_notice: arrivalNotice!,
                pre_alert: preAlert!,
                customs_instruction: customsInstruction!,
                delivery_request,
                creator: user.data!,
              });

              setStep(step + 1);
            } else if (step === 2) {
              await createOrUpdate();
              if (values.action === 'submit') {
                onSuccess?.('submitCustomsRequest');

                await putCustomsRequest.mutateAsync({ submission_target });
              } else if (values.action === 'urlSubmit') {
                const { url } = await customsRequestURLSubmit.mutateAsync({ submission_target });
                copy(url);

                onSuccess?.('urlSubmitCustomsRequest');
              } else {
                onSuccess?.('createCustomsRequest');
              }

              onClose();
            }
          },
        })}
        className={classes.form}
      >
        {[renderFormStep0, renderFormStep1, renderFormStep2][step]?.()}

        <ActionableDialogActions justifyContent="center">
          <DebugForm form={form} />

          <Button
            color="primary"
            size="small"
            shape="round"
            type="button"
            text={formStrings.prev}
            style={{ width: 90 }}
            onClick={() => {
              if (step === 0) {
                onClose();
              } else {
                setStep(step - 1);
              }
            }}
          />

          {step === 0 && (
            <Button
              color="primary"
              size="small"
              shape="round"
              style={{ width: 90 }}
              text={formStrings.next}
              type="submit"
            />
          )}

          {step === 1 && (
            <Button
              color="primary"
              size="small"
              shape="round"
              style={{ width: 90 }}
              text={strings.preview}
              type="submit"
            />
          )}

          {step === 2 && (
            <Button
              color="primary"
              size="small"
              shape="round"
              type="submit"
              style={{ width: 90 }}
              text={strings.submit()[type]}
              onClick={() => fields.action.setOutput('save')}
            />
          )}

          {step === 2 && (
            <Button
              color="accent"
              size="small"
              shape="round"
              type="submit"
              style={{ width: 90 }}
              text={strings.request}
              onClick={() => fields.action.setOutput('submit')}
            />
          )}

          {step === 2 && (
            <Button
              color="accent"
              size="small"
              shape="round"
              type="submit"
              style={{ width: 120 }}
              text={strings.copyLink}
              onClick={() => fields.action.setOutput('urlSubmit')}
            />
          )}
        </ActionableDialogActions>
      </form>
    </>
  );
});

export interface CustomsRequestFormDialogProps extends CustomsRequestFormDialogContextValuesBase {}

export const CustomsRequestFormDialog: React.FC<CustomsRequestFormDialogProps> = (props) => {
  const { type, open, onClose, d2dNo, customsInstruction, customsRequest } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  // Ensure we reset the form on open
  const dialogContentKey = useDeferredValue(String(open));

  const bookingInquiry = useBookingInquiryDetailByD2dNo(d2dNo);
  const preAlert = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });
  const arrivalNotice = dashboardArrivalNoticeHooks.useGet(
    '/dashboard/arrival-notice/:id',
    {
      params: { id: preAlert.data?.arrival_notice_id! },
    },
    {
      enabled: !!preAlert.data?.arrival_notice_id,
    },
  );
  const calculatedDates = useQuery(
    ['workday', preAlert.data?.eta!],
    async () => {
      const res = await workdayApis.client.post('/workday', [
        { base_date: preAlert.data?.eta!, diff_days: 2 },
        { base_date: preAlert.data?.eta!, diff_days: 7 },
      ]);
      return {
        // default: ETA + 2 Days
        cargo_move_in_date: res[0],
        // default：搬入日 + 5 Days
        free_time: res[1],
        // default：ETA + 2 Days
        tentative_pickup_date: res[0],
      };
    },
    {
      enabled: !!preAlert.data?.eta && type === 'create',
    },
  );

  const maybeInitialValues = useMemo(() => {
    if (type === 'create') {
      if (preAlert.data && bookingInquiry.data && calculatedDates.data) {
        return resolveCustomsRequestInitialValues(
          preAlert.data,
          bookingInquiry.data,
          customsInstruction,
          calculatedDates.data,
        );
      }
      return undefined;
    } else {
      return {
        _tag: 'Right',
        right: new CustomsRequestHelper(customsRequest!).toForm(),
      } as const;
    }
  }, [
    preAlert.data,
    bookingInquiry.data,
    customsInstruction,
    customsRequest,
    calculatedDates.data,
  ]);

  const contextValue = useShallowMemo(
    (): CustomsRequestFormDialogContextValues => ({
      ...props,
      initialValues: maybeInitialValues?._tag === 'Right' ? maybeInitialValues?.right : undefined,
      bookingInquiry: bookingInquiry.data,
      arrivalNotice: arrivalNotice.data || undefined,
      preAlert: preAlert.data,
      customsInstruction,
    }),
  );

  const renderErrors = () => {
    if (maybeInitialValues?._tag === 'Left') {
      return (
        <>
          <div
            className={clsx(classes.middleText, classes.textGray500)}
            style={{ padding: '2rem' }}
          >
            {strings.cannotCreateCustomsRequest}

            <ul>
              {maybeInitialValues?.left.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>

            <Button
              color="primary"
              style={{ width: 100, marginTop: 50 }}
              text="OK"
              size="small"
              type="button"
              onClick={onClose}
            />
          </div>
        </>
      );
    }
  };

  return (
    <CustomsRequestFormDialogContext.Provider value={contextValue}>
      <ActionableDialog open={open} onClose={onClose} size="lg" source={import.meta.url}>
        <QueryContainer queries={[bookingInquiry, arrivalNotice, preAlert, calculatedDates]}>
          {maybeInitialValues?._tag === 'Left' ? (
            renderErrors()
          ) : (
            <CustomsRequestFormDialogContent key={dialogContentKey} />
          )}
        </QueryContainer>
      </ActionableDialog>
    </CustomsRequestFormDialogContext.Provider>
  );
};
